import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {TimeZone, User, userStatusService} from '../../../domain';
import {Disposers, HttpPromiseState, toastrHelper} from '../../../lib/helpers';
import {timeZoneLoaders} from '../../../domain/loaders/timeZoneLoaders';
import {UpdatePasswordStore} from './parts/UpdatePasswordStore';
import {UpdateTimeZoneStore} from './parts/UpdateTimeZoneStore';

export class CurrentUserSceneStore {
  @observable
  mode: 'view' | 'update-password' | 'update-timezone';

  @computed
  get user(): User | null {
    return userStatusService.user;
  };

  @observable.ref
  timeZonesState: HttpPromiseState<readonly TimeZone[]> | null;

  @observable.ref
  updateTimeZone: UpdateTimeZoneStore | null;
  @observable.ref
  updatePassword: UpdatePasswordStore | null;

  private readonly disposers = new Disposers();

  constructor() {
    makeObservable(this);
    this.mode = 'view';
    this.timeZonesState = null;
    this.updateTimeZone = this.updatePassword = null;
    this.setup();
  }

  @action
  loadTimeZones = () => {
    if (this.timeZonesState?.isPending === true)
      return;
    this.timeZonesState = timeZoneLoaders.list();
  };

  @action
  beginUpdateTimeZone = () => {
    if (this.timeZonesState?.isResolved !== true)
      this.loadTimeZones();
    this.mode = 'update-timezone';
    this.updateTimeZone = new UpdateTimeZoneStore();
  };

  @action
  completeUpdateTimeZone = () => {
    this.updateTimeZone?.complete();
  };

  @action
  beginUpdatePassword = () => {
    this.mode = 'update-password';
    this.updatePassword = new UpdatePasswordStore();
  };

  @action
  completeUpdatePassword = () => {
    this.updatePassword?.complete();
  };

  @action
  cancel = () => {
    this.setViewMode();
  };

  @action
  setViewMode() {
    this.mode = 'view';
    this.updateTimeZone = this.updatePassword = null;
  }

  /****************************************************************************/

  private setup() {
    this.disposers.add(
      reaction(
        () => [this.mode, this.user, this.timeZonesState?.state],
        this.initTimeZoneStore
      ),
      reaction(
        () => this.updatePassword?.save?.state,
        this.afterPasswordUpdated
      ),
      reaction(
        () => this.updateTimeZone?.save?.state,
        this.afterTimezoneUpdated
      ));
  }

  @action
  private initTimeZoneStore = () => {
    if (this.user === null || this.timeZonesState?.isResolved !== true)
      return;
    this.updateTimeZone?.init(this.user, this.timeZonesState.value);
  };

  @action
  private afterPasswordUpdated = () => {
    if (this.updatePassword?.save?.isResolved !== true)
      return;

    toastrHelper.success(this.updatePassword.save.value);
    this.setViewMode();
  };

  @action
  private afterTimezoneUpdated = () => {
    if (this.updateTimeZone?.save?.isResolved !== true)
      return;
    toastrHelper.success(this.updateTimeZone.save.value);
    userStatusService.refreshWhoAmI();
    this.setViewMode();
  };

  dispose = () => {
    this.disposers.dispose();
  };
}
